import { OperationMS } from '../../types';
import { CommandeAnalyse } from '../analyse/types';
import { WEIGHT_TECHNIQUE, WEIGHT_FINANCIERE } from '../analyse/scoring';
import { getRetenuCommande } from './marcheAttribution';

// Construction de l'aperçu mail de notification (retenu / non-retenu). Reproduit verbatim le
// gabarit legacy generateMailPreview (HTML brut consomme par le composant MailPreview). La
// ponderation vient des constantes domaine (0.55 / 0.45), plus de litteraux dans le template.

// Echappement HTML : les valeurs API (noms d'entite/opero, interlocuteur...) sont interpolees
// dans une string HTML ensuite injectee (iframe). Sans echappement = surface XSS stockee.
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fr2 = (n: number): string =>
  n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const noteOrX = (n: number | null | undefined): string =>
  n !== null && n !== undefined ? n.toString() : 'X';

const noteGlobaleOrX = (tech: number | null | undefined, fin: number | null | undefined): string =>
  tech !== null && tech !== undefined && fin !== null && fin !== undefined
    ? fr2(tech * WEIGHT_TECHNIQUE + fin * WEIGHT_FINANCIERE)
    : 'X';

export const buildMailPreview = (
  commande: CommandeAnalyse,
  operation: OperationMS,
  dateEnvoi?: string | null,
): string => {
  const isRetenuMail = operation.id_commande === commande.id;
  // Titre de l'aperçu : « Envoyé le … » si la notification a déjà été envoyée, sinon « Aperçu … ».
  const titre = dateEnvoi
    ? `Envoyé le ${escapeHtml(dateEnvoi)}`
    : `Aperçu du message de notification au ${isRetenuMail ? 'retenu' : 'non-retenu'}`;
  const acheteurNom = escapeHtml(operation.organisme?.nom || 'NOM_ACHETEUR');
  const operationNom = escapeHtml(operation.nom || "Nom de l'opération");
  const numero = escapeHtml(String(commande.numero || operation.numero || 'XXX'));
  const date = new Date().toLocaleDateString('fr-FR');
  const montant = commande.prix_total != null ? fr2(Number(commande.prix_total)) : '0,00';
  // L'interlocuteur peut vivre a TROIS endroits selon la vue (le template est partage acheteur/presta) :
  //   1. organisme.contact_operationnel (vue acheteur, comme OperationAcheteurRecap qui l'affiche bien)
  //   2. organisme.acheteur (vue acheteur, repli)
  //   3. operation.acheteur AU NIVEAU OPERATION (vue PRESTA : commande.operation.acheteur, pas sous organisme)
  // L'ancien code ne lisait que (2) en exigeant prenom ET nom -> "INTERLOCUTEUR" vide cote presta.
  const contact = operation.organisme?.contact_operationnel;
  const orgAcheteur = operation.organisme?.acheteur;
  const opAcheteur = (operation as { acheteur?: { prenom?: string; nom?: string; telephone?: string } }).acheteur;
  const interlocuteurNom =
    contact?.nom ||
    [orgAcheteur?.prenom, orgAcheteur?.nom].filter(Boolean).join(' ') ||
    [opAcheteur?.prenom, opAcheteur?.nom].filter(Boolean).join(' ');
  const interlocuteur = escapeHtml(interlocuteurNom || 'INTERLOCUTEUR');
  const interlocuteurTel = escapeHtml(
    contact?.telephone || orgAcheteur?.telephone || opAcheteur?.telephone || '00 00 00 00 00',
  );

  const noteTechniqueVal = commande.note_technique?.note_technique ?? null;
  const noteFinanciereVal = commande.note_financiere ?? null;
  const noteTechnique = noteOrX(noteTechniqueVal);
  const noteFinanciere = noteOrX(noteFinanciereVal);
  const noteGlobale = noteGlobaleOrX(noteTechniqueVal, noteFinanciereVal);

  const best = getRetenuCommande(operation);
  const bestPrestaName = escapeHtml(best?.contrat?.entite?.nom || 'Entreprise retenue');
  const bestNoteTechVal = best?.note_technique?.note_technique ?? null;
  const bestNoteFinVal = best?.note_financiere ?? null;
  const bestPrestaNoteTechnique = noteOrX(bestNoteTechVal);
  const bestPrestaNoteFinanciere = noteOrX(bestNoteFinVal);
  const bestPrestaNoteGlobale = noteGlobaleOrX(bestNoteTechVal, bestNoteFinVal);

  const styles = `
      body {
        font-family: "Segoe UI", Roboto, Arial, sans-serif;
        background-color: #f7f9fb;
        padding: 40px;
        color: #333;
        line-height: 1.6;
      }
      .mail-container {
        max-width: 700px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 40px 50px;
      }
      .header-container {
        max-width: 700px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 40px 50px;
      }
      h1 { font-size: 20px; margin-bottom: 25px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
      h2 {
        background-color: #ffffff;
        max-width: 700px;
        margin: 0 auto 20px auto;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        padding: 20px 50px;
      }
      p { margin: 14px 0; font-size: 15px; }
      strong { color: #000; }
      .footer { margin-top: 40px; padding-top: 15px; border-top: 1px solid #eee; font-size: 14px; color: #666; }`;

  if (isRetenuMail) {
    return `
<html>
  <head>
    <meta charset="utf-8" />
    <style>${styles}
      span {
        padding: 1rem 1.5rem;
        font-size: 1.1rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: #f97316 !important;
        color: #fffdfc;
        border: none;
        border-radius: 50px;
        font-weight: 700;
        text-decoration: none;
        margin: 1.5rem auto 0 auto !important;
      }
    </style>
  </head>
  <body>
    <div class="header-container">
      <h2>${titre}</h2>
    </div>
    <div class="mail-container">
      <h1 class="object">Objet : Notification du Marché subséquent N°${numero} pour l’opération ${operationNom} de l’acheteur ${acheteurNom}.</h1>
      <p>Cher prestataire,</p>
      <p>
        Par le présent mail, nous avons le plaisir de vous notifier le marché subséquent <strong>N°${numero}</strong> du <strong>${date}</strong> pour l’opération <strong>${operationNom}</strong>, d’un montant de <strong>${montant}€</strong>, et le rendons exécutoire à partir de ce jour.
      </p>
      <p>
        L’interlocuteur identifié est <strong>${interlocuteur}</strong> dont le N° de tél est <strong>${interlocuteurTel}</strong>. N’hésitez pas à le joindre.
      </p>
      <p>
        Vous pouvez retrouver l’intégralité du suivi concernant ce bon sur votre espace en ligne et vous rappelons qu’il vous appartient de maintenir à jour vos pièces telles qu’assurances, attestations, etc…
      </p>
      <span>Voir le Marché subséquent</span>
      <div class="footer">
        <p>Cordialement,<br/><strong>L’équipe Centralis</strong></p>
      </div>
    </div>
  </body>
</html>
`;
  }

  return `
<html>
  <head>
    <meta charset="utf-8" />
    <style>${styles}</style>
  </head>
  <body>
    <div class="header-container">
      <h2>${titre}</h2>
    </div>
    <div class="mail-container">
      <h1 class="object">Objet : Offre non retenue pour la consultation de l’opération n°${numero} de la part de l’acheteur ${acheteurNom}.</h1>
      <p>Cher prestataire,</p>
      <p>
        Nous avons le regret de vous informer que votre offre n’a pas été retenue dans le cadre du marché subséquent pour l’opération ${numero} de la part de l’acheteur ${acheteurNom}.
      </p>
      <p>
        Nous portons à votre connaissance que l’entreprise suivante a été retenue :
        <strong>${bestPrestaName}</strong>
        avec une note de <strong>${bestPrestaNoteGlobale}/10</strong> (dont ${bestPrestaNoteTechnique}/10 pour la valeur technique,
        et ${bestPrestaNoteFinanciere}/10 pour la valeur financière).
      </p>
      <p>
        En ce qui concerne votre entreprise, vous avez obtenu la note de <strong>${noteGlobale}/10</strong>
        (dont ${noteTechnique}/10 pour la valeur technique, et ${noteFinanciere}/10 pour la valeur financière).
      </p>
      <p>
        L’acheteur et notre Centrale tenions à vous remercier de votre participation.
        Nous ne manquerons pas de revenir vers vous pour les prochaines consultations.
      </p>
      <div class="footer">
        <p>Cordialement,<br/><strong>L’équipe Centralis</strong></p>
      </div>
    </div>
  </body>
</html>
`;
};
